import { useSyncExternalStore } from 'react';

/**
 * Si el servidor contesta. No si el teléfono cree tener antena.
 *
 * ## Por qué no alcanza `navigator.onLine`
 *
 * Se descubrió en el teléfono, no leyendo código. Con el aparato en MODO AVIÓN,
 * `navigator.onLine` devolvió `true`: la pantalla de Liquidación —que ya tenía
 * puesto el cartel de «necesita conexión»— se dibujó entera y con un
 * `TypeError: Failed to fetch` adentro. Sólo puede pasar si la aplicación creía
 * tener red.
 *
 * El WebView de Android no siempre recibe los avisos de conectividad del
 * sistema, así que `navigator.onLine` responde una pregunta todavía más chica
 * de la que ya respondía: no dice si hay servidor, ni siquiera si hay antena;
 * dice lo último que el WebView escuchó.
 *
 * ## Qué se usa en su lugar
 *
 * Lo que de verdad pasó con las peticiones. Cada llamada a Supabase informa si
 * llegó o si falló por red, y de eso sale el estado. `navigator.onLine` se
 * conserva únicamente como señal NEGATIVA rápida —si el navegador ya sabe que
 * no hay red, no hace falta esperar a que falle un pedido— pero su `true` no
 * prueba nada y no se le cree.
 *
 * Es la diferencia entre preguntar «¿tenés antena?» y «¿te contestaron?». En el
 * campo son cosas distintas seguido: una raya de señal que no llega a ningún
 * lado se ve exactamente igual que la conexión buena, hasta que se intenta.
 *
 * ## Dos señales, no una
 *
 * Enterarse sólo por peticiones fallidas tiene su propio costo: hasta que una
 * falla, la pantalla se queda esperando. Con un límite largo eso son varios
 * segundos mirando algo detenido para descubrir lo que el teléfono ya sabía.
 *
 * Así que se combinan dos cosas distintas, y se las trata como distintas:
 *
 *   · **el sistema operativo** dice si hay interfaz de red. En la aplicación
 *     empaquetada eso lo contesta el plugin nativo, que SÍ se entera del modo
 *     avión —a diferencia del `navigator.onLine` del WebView—. Su «no» es
 *     inmediato y definitivo: sin interfaz no hay a dónde mandar nada, y se
 *     entra al camino sin conexión sin esperar ningún vencimiento.
 *
 *   · **el servidor** contesta o no contesta. Su «sí» es lo único que habilita
 *     el modo normal.
 *
 * Tener Wi-Fi no es tener servidor. Un camión con antena propia, un portal
 * cautivo de hotel o Supabase caído dan «sí» en la primera señal y «no» en la
 * segunda, y quien manda ahí es la segunda.
 */

/** Lo que se sabe del servidor ahora mismo. */
export type Conectividad =
  /** Alguna petición llegó recién. */
  | 'con-servidor'
  /** Alguna petición falló por red, o el navegador dice que no hay. */
  | 'sin-servidor'
  /** Todavía no se intentó nada. Se asume que sí: no hay motivo para no. */
  | 'sin-saber';

let estado: Conectividad = 'sin-saber';
/**
 * Lo que dice el sistema operativo sobre la interfaz de red.
 *
 * `null` mientras no haya nadie que lo informe —en el navegador de escritorio,
 * siempre—. Sólo se usa su `false`: es la señal negativa rápida. Su `true`
 * significa «hay antena», que no es «hay servidor», y por eso no alcanza para
 * volver al modo normal.
 */
let redDelSistema: boolean | null = null;
const oyentes = new Set<() => void>();

function avisar() {
  for (const o of oyentes) o();
}

function cambiar(nuevo: Conectividad) {
  if (estado === nuevo) return;
  estado = nuevo;
  avisar();
}

/**
 * Una petición llegó al servidor.
 *
 * Vale aunque el servidor haya contestado un error: un 401 o un 500 significan
 * que HAY servidor. Lo que se está midiendo es si se puede hablar con él, no si
 * lo que se le pidió salió bien.
 */
export function registrarRespuesta(): void {
  cambiar('con-servidor');
}

/** Una petición no llegó: no hay con quién hablar. */
export function registrarFalloDeRed(): void {
  cambiar('sin-servidor');
}

/**
 * El sistema operativo cambió de opinión sobre la red.
 *
 * Lo llama el plugin nativo, que es quien de verdad se entera del modo avión.
 * Su `false` corta por lo sano: no hace falta que falle una petición para saber
 * que no hay a dónde mandarla.
 *
 * Su `true` NO devuelve al modo normal por sí solo —deja el estado en «no
 * sé»—, y además despierta a quien estuviera esperando la vuelta de la señal.
 * Ese aviso se emite acá y no se deja librado al evento `online` del navegador
 * porque en el WebView de Android ese evento no siempre llega; si la cola
 * dependiera de él, los trabajos cargados sin señal se quedarían esperando un
 * aviso que nunca sucede.
 */
export function registrarRedNativa(hay: boolean): void {
  if (redDelSistema === hay) return;
  const volvio = redDelSistema === false && hay;
  redDelSistema = hay;

  if (!hay) cambiar('sin-servidor');
  else if (estado === 'sin-servidor') cambiar('sin-saber');
  avisar();

  if (volvio && typeof window !== 'undefined') window.dispatchEvent(new Event('online'));
}

/** El sistema operativo dice que no hay interfaz de red. No es una sospecha. */
export function sinRedDelSistema(): boolean {
  return redDelSistema === false;
}

/**
 * ¿Este error es «no hay red», o es el servidor diciendo que no?
 *
 * Se centraliza acá y no en cada pantalla porque la distinción cambia lo que
 * hay que mostrar: un fallo de red se traduce a un estado de producto —«sin
 * conexión»— y un rechazo del servidor se cuenta tal como vino, porque dice qué
 * hacer.
 *
 * Los mensajes son los que producen los distintos navegadores y motores para lo
 * mismo. Ninguno de ellos debería llegar nunca a una pantalla.
 */
export function esErrorDeRed(e: unknown): boolean {
  if (e == null) return false;
  const mensaje = (
    typeof e === 'string' ? e : ((e as { message?: string })?.message ?? '')
  ).toLowerCase();
  if (!mensaje) return false;

  return (
    mensaje.includes('failed to fetch') ||
    mensaje.includes('networkerror') ||
    mensaje.includes('network request failed') ||
    mensaje.includes('load failed') ||
    mensaje.includes('err_internet_disconnected') ||
    mensaje.includes('err_network') ||
    mensaje.includes('err_name_not_resolved') ||
    mensaje.includes('err_address_unreachable') ||
    mensaje.includes('fetch failed') ||
    mensaje.includes('the internet connection appears to be offline') ||
    // `AbortError` por vencimiento propio: se intentó y no llegó a tiempo.
    mensaje.includes('signal timed out') ||
    mensaje.includes('the operation was aborted')
  );
}

/**
 * El servidor rechazó el pedido por permisos.
 *
 * Es lo que contesta PostgREST cuando quien llama no es `authenticated`: un
 * `42501` de PostgreSQL —«permission denied for function …»— o un `PGRST301`,
 * que es el token vencido o ilegible.
 */
export function esRechazoDePermiso(e: unknown): boolean {
  if (e == null || typeof e !== 'object') return false;
  const codigo = (e as { code?: string }).code;
  if (codigo === '42501' || codigo === 'PGRST301') return true;
  const mensaje = ((e as { message?: string }).message ?? '').toLowerCase();
  return mensaje.includes('permission denied for') || mensaje.includes('jwt expired');
}

/**
 * Si la credencial con la que corre la aplicación está sin confirmar.
 *
 * El `AuthProvider` tiene un modo de gracia: cuando el token no se puede
 * renovar pero hay credencial guardada y usable, deja seguir trabajando hasta
 * siete días. Es deliberado —sin eso, un veterinario en un campo sin antena
 * queda afuera de su propia aplicación— y tiene el costo escrito desde el
 * primer día: si la sesión además está revocada del lado del servidor, el
 * servidor rechaza cada pedido igual.
 *
 * Eso es justo lo que pasó: la pantalla mostraba el nombre arriba y cada
 * consulta volvía con «permission denied for function search_clients».
 *
 * Esta bandera existe para poder decirlo. **No se adivina por el texto del
 * error**: el mismo mensaje puede venir de un permiso mal configurado en la
 * base, y mandar a alguien a volver a entrar por eso sería mandarlo a perder
 * el tiempo. Sólo se afirma que la sesión venció cuando la aplicación sabe
 * que está corriendo con una credencial que el servidor no confirmó.
 */
let credencialSinConfirmar = false;

export function marcarCredencialSinConfirmar(valor: boolean): void {
  credencialSinConfirmar = valor;
}

/**
 * El error, dicho para una persona.
 *
 * Ninguna pantalla arma su propio texto: si lo hicieran, cada una traduciría
 * distinto y alguna se olvidaría. `TypeError: Failed to fetch` no le dice nada
 * a nadie —salió en tres pantallas del teléfono y era siempre lo mismo— y
 * además esconde que el trabajo se puede seguir cargando igual.
 */
export function mensajeDeError(e: unknown, queSeIntentaba?: string): string {
  if (esErrorDeRed(e)) {
    return queSeIntentaba
      ? `Sin conexión. ${queSeIntentaba}`
      : 'Sin conexión con el servidor. Lo que cargues se guarda en el teléfono.';
  }

  // `permission denied for function search_clients` es un mensaje de
  // PostgreSQL sobre una función interna. No le dice nada a nadie, y lo que
  // hay que hacer —volver a entrar— no está en ninguna parte de ese texto.
  if (credencialSinConfirmar && esRechazoDePermiso(e)) {
    return 'Se venció tu sesión. Cerrá sesión y volvé a entrar para seguir.';
  }

  const m = (e as { message?: string })?.message;
  // El mensaje del servidor va tal cual: es lo único que permite entender qué
  // corregir. Traducirlo a «hubo un problema» le saca eso a quien lo lee.
  return m && m.trim() !== '' ? m : 'No se pudo completar la operación.';
}

/** Marca el estado a partir de un error, para que lo vea toda la aplicación. */
export function registrarError(e: unknown): void {
  if (esErrorDeRed(e)) registrarFalloDeRed();
  else registrarRespuesta();
}

// ---------------------------------------------------------------------------
// Lo que consumen los componentes
// ---------------------------------------------------------------------------

function suscribir(avisame: () => void) {
  oyentes.add(avisame);
  // El evento del navegador sigue sirviendo como aviso rápido. Su `offline` se
  // toma por bueno; su `online` sólo devuelve al estado «no sé», porque tener
  // interfaz de red no es tener servidor.
  const seFue = () => cambiar('sin-servidor');
  const volvio = () => cambiar('sin-saber');
  window.addEventListener('offline', seFue);
  window.addEventListener('online', volvio);
  return () => {
    oyentes.delete(avisame);
    window.removeEventListener('offline', seFue);
    window.removeEventListener('online', volvio);
  };
}

function leer(): Conectividad {
  // Primero el sistema operativo: es el único que sabe del modo avión.
  if (redDelSistema === false) return 'sin-servidor';
  // El `false` del navegador también se cree; su `true`, no.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'sin-servidor';
  return estado;
}

/**
 * Avisa en cuanto se sepa que no hay servidor.
 *
 * Existe para el arranque en frío. Ahí no alcanza con que las peticiones
 * fallen: `getSession()` con el token vencido intenta renovarlo, y esa espera
 * es la que deja la aplicación en «Cargando…». Con esto, apenas el sistema
 * operativo dice que no hay red —o apenas una petición falla— se corta la
 * espera y se abre con la credencial local, que es justo para esto.
 *
 * Devuelve también cómo dejar de escuchar: si ganó la otra mitad de la carrera,
 * esto queda esperando algo que quizá no pase nunca.
 */
export function alSaberQueNoHay(): { promesa: Promise<void>; cancelar: () => void } {
  let cancelar = () => {};
  const promesa = new Promise<void>((listo) => {
    if (leer() === 'sin-servidor') return listo();
    const quitar = suscribir(() => {
      if (leer() !== 'sin-servidor') return;
      quitar();
      listo();
    });
    cancelar = quitar;
  });
  return { promesa, cancelar };
}

/** El estado tal cual, para quien necesite distinguir «no sé» de «sí». */
export function useConectividad(): Conectividad {
  return useSyncExternalStore(suscribir, leer, () => 'sin-saber');
}

/**
 * ¿Estamos sin servidor?
 *
 * Mientras no se sepa, se contesta que no: la aplicación arranca intentando, y
 * bloquear pantallas por las dudas sería peor que descubrirlo al primer fallo.
 */
export function useSinConexion(): boolean {
  return useConectividad() === 'sin-servidor';
}

/**
 * ¿No hay red, de verdad?
 *
 * Distinto de `useSinConexion`, que mezcla dos cosas: el modo avión y «una
 * petición falló recién». Para decidir si se puede trabajar, esa mezcla está
 * bien —ante la duda, modo sin conexión—. Para AFIRMARLE a alguien «ahora no
 * hay señal», no: un pedido que falló mientras la red volvía deja ese estado
 * puesto, y el cartel termina contradiciendo a las barritas de la pantalla.
 *
 * Acá sólo cuentan las fuentes que no se equivocan cuando dicen que no: el
 * sistema operativo, y el `false` del navegador.
 */
export function useSinRedDeVerdad(): boolean {
  return useSyncExternalStore(
    suscribir,
    () => sinRedDelSistema() || (typeof navigator !== 'undefined' && navigator.onLine === false),
    () => false,
  );
}

/** Igual que `useSinConexion`, para código que no es un componente. */
export function sinConexionAhora(): boolean {
  return leer() === 'sin-servidor';
}

/** Sólo para pruebas: volver al estado inicial. */
export function reiniciarConectividad(): void {
  estado = 'sin-saber';
  redDelSistema = null;
  avisar();
}
